/**
 * VÉRAULT Member Suite — Home (Phase 1a · foundation only)
 * ──────────────────────────────────────────────────────────
 * The living overview of a member's travel world. Not a dashboard,
 * not a CRM index — an editorial reading room.
 *
 * Phase 1a builds the LAYOUT SHELL only:
 *   ▸ Cinematic welcome band
 *   ▸ Active experience spotlight (with a calm empty state)
 *   ▸ Quiet "Around the studio" strip (countdowns / messages / notices)
 *   ▸ Editorial rules between bands so the page breathes
 *
 * Phase 1b adds the live data feeds + hidden-destination veil.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Bell, BellOff, Compass, MessageSquare, Sparkles } from "lucide-react";
import {
  Heading,
  EditorialRule,
  PortalButton,
  api,
  motion as portalMotion,
} from "../../index";
import { useAuth } from "../../../contexts/AuthContext";
import { useDestinationVeil } from "../../../hooks/useDestinationVeil";
import { useWebPush } from "../../hooks/useWebPush";

const { fadeInUp, stagger } = portalMotion;

// ─── Wave 32 · Phase 2 — Featured Reminders highlight band ───────────
function FeaturedRemindersBand({ tripId }) {
  const [reminders, setReminders] = React.useState([]);
  React.useEffect(() => {
    if (!tripId) return;
    let alive = true;
    api.get(`/client-portal/experiences/${tripId}/travel-prep-reminders`)
      .then(({ data }) => alive && setReminders(data?.reminders || []))
      .catch(() => alive && setReminders([]));
    return () => { alive = false; };
  }, [tripId]);
  if (reminders.length === 0) return null;
  return (
    <motion.section
      variants={fadeInUp}
      className="border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/[0.05] rounded-md p-5 sm:p-6 space-y-3"
      data-testid="member-home-featured-reminders"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        <h2 className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
          Quiet reminders
        </h2>
      </div>
      <ul className="space-y-2.5">
        {reminders.slice(0, 4).map((r) => (
          <li
            key={r.id}
            className="flex items-start justify-between gap-3 flex-wrap"
            data-testid={`member-home-featured-reminder-${r.id}`}
          >
            <div className="min-w-0">
              <p className="font-serif text-[15px] sm:text-[16px] text-[hsl(var(--foreground))] leading-snug">
                {r.text || r.section_title}
              </p>
              {r.page_title && (
                <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                  {r.page_title}
                </p>
              )}
            </div>
            <Link
              to={`/portal/client/experiences/${tripId}/travel-prep`}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-secondary))] hover:text-[hsl(var(--accent-gold))] transition-colors"
            >
              Open
              <ArrowUpRight className="size-3" strokeWidth={1.5} />
            </Link>
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

// ─── Welcome band ────────────────────────────────────────────────
function WelcomeBand({ name }) {
  return (
    <motion.div variants={fadeInUp} data-testid="member-home-welcome">
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--foreground-muted))]">
        Member Suite
      </p>
      <Heading
        level="display"
        className="mt-3 !text-[40px] sm:!text-[52px] lg:!text-[68px] !leading-[1.05]"
      >
        {name ? <>Welcome back,<br />{name}.</> : <>Welcome back.</>}
      </Heading>
      <p className="mt-5 font-accent italic text-[16px] sm:text-[18px] text-[hsl(var(--foreground-secondary))] max-w-xl leading-relaxed">
        The shape of your travel life, in one calm view. Begin where it feels
        most natural.
      </p>
    </motion.div>
  );
}

// ─── Active experience spotlight ─────────────────────────────────
//
// Feb 2026 — readability fix. The previous version fell back to a
// near-white gradient when no hero image was set, which made the
// light editorial text wash out (see user-reported "Welcome back,
// Ikenna" screenshot). The card now defaults to a solid espresso
// background and only renders an image when `suite_home_hero.mode`
// is "image" AND a real URL exists. A full overlay guarantees text
// contrast every time.
const ESPRESSO_DEFAULT = "#3a261b";

function SpotlightCard({ trip }) {
  const veil = useDestinationVeil(trip);
  const heroCfg = trip?.suite_home_hero || {};
  const heroMode = (heroCfg.mode || "solid").toLowerCase();
  const solidColor = heroCfg.solid_color || ESPRESSO_DEFAULT;
  const heroImage =
    heroMode === "image"
      ? (heroCfg.image_url || veil.image || null)
      : null;
  const overlayStrength = typeof heroCfg.overlay_strength === "number"
    ? Math.min(0.95, Math.max(0.35, heroCfg.overlay_strength))
    : 0.6;
  const [stage, setStage] = React.useState(null);

  React.useEffect(() => {
    if (!trip?.id) return;
    let alive = true;
    api.get(`/client-portal/trips/${trip.id}/stage`)
      .then(({ data }) => alive && setStage(data))
      .catch(() => alive && setStage(null));
    return () => { alive = false; };
  }, [trip?.id]);

  const expTitle = stage?.experience_title || veil.label;
  const dateLine = stage?.dates || veil.dateLabel;
  // "Hide the proposal": when the stage points at the proposal / flight-approval,
  // send the hero straight into the (already-published) itinerary instead.
  const isProposalStage =
    (stage?.cta_path || "").includes("/proposal") ||
    /flight|proposal|approval/i.test(stage?.eyebrow || "") ||
    /flight|proposal|approval/i.test(stage?.status_text || "");
  const eyebrow = isProposalStage
    ? "Your itinerary is ready"
    : (stage?.eyebrow || (veil.veiled ? "Awaiting reveal" : "The next experience"));
  const statusText = isProposalStage ? "Your itinerary is ready" : (stage?.status_text || eyebrow);
  const cta = isProposalStage ? "Open your itinerary" : (stage?.cta_label || "Open your experience");
  const ctaPath = isProposalStage
    ? `/portal/client/experiences/${trip.id}/itinerary`
    : (stage?.cta_path || `/portal/client/experiences/${trip.id}`);
  const ctaActive = isProposalStage ? true : Boolean(stage?.cta_path);

  return (
    <Link
      to={ctaPath}
      className="group block relative overflow-hidden rounded-md isolate"
      data-testid="member-home-spotlight"
      data-stage={stage?.stage || ""}
      data-hero-mode={heroImage ? "image" : "solid"}
    >
      {/* Feb 2026 mobile fix — replaced rigid aspect-ratio + absolutely-
          positioned bottom content (which clipped the title when copy +
          progress + CTA overflowed the height) with a natural flow card:
          content sits in normal layout flow on mobile, and only switches
          to the editorial absolute-bottom composition on sm+ screens
          where the wider aspect ratio has enough vertical room. */}
      <div
        className="relative min-h-[440px] sm:min-h-0 sm:aspect-[21/9] flex sm:block"
        style={{ backgroundColor: solidColor }}
      >
        {heroImage && (
          <img
            src={heroImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-[1800ms] group-hover:scale-[1.04]"
            loading="eager"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}
        {/* Full editorial overlay — readability comes first. When an
            image is used, this overlay darkens the entire surface so
            light text stays legible top to bottom. When no image, the
            overlay is a soft gradient that keeps the espresso warmth. */}
        <div
          className="absolute inset-0"
          style={
            heroImage
              ? { background: `linear-gradient(180deg, rgba(0,0,0,${overlayStrength * 0.7}) 0%, rgba(0,0,0,${overlayStrength}) 60%, rgba(0,0,0,${Math.min(0.96, overlayStrength + 0.15)}) 100%)` }
              : { background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.25) 100%)" }
          }
        />

        <div
          className="relative w-full self-end sm:absolute sm:inset-x-0 sm:bottom-0 px-6 py-8 sm:p-10 text-white max-w-3xl"
          data-testid="member-home-spotlight-content"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#e9d4ad]"
             data-testid="member-home-spotlight-eyebrow">
            {(statusText || "").toString().toUpperCase()}
          </p>
          <h3 className="mt-3 font-serif text-[26px] sm:text-4xl tracking-tight leading-[1.1] text-white"
              data-testid="member-home-spotlight-title">
            {expTitle || cta}
          </h3>
          {dateLine && (
            <p className="mt-2 font-accent italic text-[14px] sm:text-[15px] text-white/85"
               data-testid="member-home-spotlight-dates">
              {dateLine}
            </p>
          )}
          {(isProposalStage ? "Your day-by-day itinerary is ready — open it to explore every day, with live weather, maps and details." : stage?.primary_copy) && (
            <p className="mt-4 font-serif text-[14px] sm:text-[15px] text-white/90 max-w-xl leading-relaxed"
               data-testid="member-home-spotlight-copy">
              {isProposalStage
                ? "Your day-by-day itinerary is ready — open it to explore every day, with live weather, maps and details."
                : stage.primary_copy}
            </p>
          )}
          {!isProposalStage && stage?.progress_text && (
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.24em] text-white/75"
               data-testid="member-home-spotlight-progress">
              {stage.progress_text}
            </p>
          )}
          <span className={"mt-6 inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.24em] text-[#e9d4ad] " +
                (ctaActive ? "group-hover:text-white transition-colors" : "opacity-60")}
                data-testid="member-home-spotlight-cta">
            {ctaActive ? cta : "We'll let you know when it's ready"}
            {ctaActive && <ArrowUpRight className="size-3.5" strokeWidth={1.5} />}
          </span>
        </div>
      </div>
    </Link>
  );
}

function SpotlightEmpty() {
  return (
    <div
      className="relative overflow-hidden rounded-md border border-[rgba(0,0,0,0.04)] bg-gradient-to-br from-[hsl(var(--background-paper))] via-white to-[hsl(var(--background-paper))]"
      data-testid="member-home-spotlight-empty"
    >
      <div className="aspect-[16/10] sm:aspect-[21/9] relative flex items-end p-6 sm:p-10">
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_20%_20%,_hsl(var(--accent-gold)),_transparent_45%),radial-gradient(circle_at_80%_80%,_hsl(var(--accent-gold)),_transparent_45%)]" />
        <div className="relative">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
            Coming soon
          </p>
          <h3 className="mt-2 font-serif text-2xl sm:text-3xl tracking-tight leading-tight text-[hsl(var(--foreground))]">
            Your next experience begins here.
          </h3>
          <p className="mt-3 font-accent italic text-[14px] sm:text-[15px] text-[hsl(var(--foreground-secondary))] max-w-md">
            Your advisor is shaping something quietly. When it&apos;s ready, it will
            arrive here, fully composed.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Quiet status strip (placeholders for Phase 1b) ──────────────
function QuietCell({ eyebrow, title, hint, to, testId }) {
  const inner = (
    <>
      <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--foreground-muted))]">
        {eyebrow}
      </p>
      <p className="mt-2 font-serif text-[18px] sm:text-[20px] tracking-tight text-[hsl(var(--foreground))] leading-snug">
        {title}
      </p>
      {hint && (
        <p className="mt-1.5 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
          {hint}
        </p>
      )}
    </>
  );
  const className =
    "block py-5 sm:py-6 px-5 sm:px-6 transition-colors hover:bg-[hsl(var(--background-paper))]/60 first:rounded-t-md last:rounded-b-md sm:first:rounded-l-md sm:first:rounded-tr-none sm:last:rounded-r-md sm:last:rounded-bl-none";
  return to ? (
    <Link to={to} className={className} data-testid={testId}>{inner}</Link>
  ) : (
    <div className={className} data-testid={testId}>{inner}</div>
  );
}

function QuietStrip({ tripsLoading, tripCount }) {
  return (
    <motion.div
      variants={fadeInUp}
      className="border border-[rgba(0,0,0,0.04)] rounded-md grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[rgba(0,0,0,0.04)] bg-white/40 backdrop-blur-[2px]"
      data-testid="member-home-strip"
    >
      <QuietCell
        eyebrow="Experiences"
        title={tripsLoading ? "Loading…" : tripCount > 0 ? `${tripCount} in your world` : "Awaiting curation"}
        hint={tripCount > 0 ? "Open the gallery." : "We'll let you know."}
        to="/portal/client/experiences"
        testId="member-home-strip-experiences"
      />
      <QuietCell
        eyebrow="Messages"
        title="A quiet inbox"
        hint="When your advisor reaches out, it lands here."
        to="/portal/client/messages"
        testId="member-home-strip-messages"
      />
      <QuietCell
        eyebrow="Connect"
        title="Book a moment"
        hint="Schedule a call with your advisor."
        to="/portal/client/connect"
        testId="member-home-strip-connect"
      />
    </motion.div>
  );
}

// ─── Page ────────────────────────────────────────────────────────
function PushEnableBanner() {
  const { supported, canSubscribe, isSubscribed, subscribe, error } = useWebPush();
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem("verault.push.dismissed") === "1"; } catch { return false; }
  });
  if (!supported || isSubscribed || dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem("verault.push.dismissed", "1"); } catch (e) { void e; }
    setDismissed(true);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-md border border-[rgba(0,0,0,0.06)] bg-white/70 dark:bg-[hsl(var(--surface-elevated))]/40 px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 flex-wrap"
      data-testid="push-enable-banner"
    >
      <Bell className="size-4 text-[hsl(var(--accent-gold))] flex-shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-[200px]">
        <p className="font-serif text-[15px] tracking-tight">Stay in the loop, calmly.</p>
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">A soft chime when your advisor reaches out or something on your itinerary needs you. Add to your home screen for the full experience.</p>
        {error && <p className="mt-1 font-mono text-[10px] text-[#8B4513]">{error}</p>}
      </div>
      {canSubscribe && (
        <PortalButton size="sm" onClick={subscribe} data-testid="push-enable-btn">
          Enable
        </PortalButton>
      )}
      <button onClick={dismiss} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]" data-testid="push-banner-dismiss">
        <BellOff className="size-3" />
      </button>
    </motion.div>
  );
}

// ─── Multi-experience gallery (Phase 1.4d · trip-scoping leak fix) ──
// When a client has more than one experience linked we must NEVER auto-
// pick one (was: "first upcoming, else first trip" — caused Jackson to
// surface when an advisor impersonated a client with multiple trips).
// Instead we render every experience as its own card so the client
// chooses explicitly which trip to enter.
function ExperienceGallery({ trips }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      data-testid="member-home-experience-gallery"
    >
      {trips.map((t) => (
        <SpotlightCard key={t.id} trip={t} />
      ))}
    </div>
  );
}

export default function MemberHome() {
  const { user } = useAuth() || {};
  const GENERIC = new Set(["owner", "admin", "advisor", "user", "client", "developer", "dev"]);
  const rawName = (user?.name || "").split(/\s+/)[0];
  const name = rawName && !GENERIC.has(rawName.toLowerCase()) ? rawName : "";

  const [trips, setTrips] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api.get("/client-portal/trips")
      .then(({ data }) => alive && setTrips(Array.isArray(data) ? data : []))
      .catch(() => alive && setTrips([]));
    return () => { alive = false; };
  }, []);

  // Phase 1.4d · NO auto-pick. If the client has exactly one experience,
  // we render its spotlight (and the featured-reminders band can safely
  // scope to that single trip). If they have multiple, the gallery lists
  // them all and the reminders band stays hidden — the user must enter a
  // specific trip first so reminders are unambiguously trip-scoped.
  const tripList = trips || [];
  const singleTrip = tripList.length === 1 ? tripList[0] : null;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-14 sm:space-y-20 pb-12"
      data-testid="member-home-root"
    >
      <WelcomeBand name={name} />

      <PushEnableBanner />

      <motion.div variants={fadeInUp}>
        <EditorialRule label={tripList.length > 1 ? "Your experiences" : "At a glance"} />
      </motion.div>

      <motion.div variants={fadeInUp}>
        {trips === null ? (
          <div className="aspect-[16/10] sm:aspect-[21/9] rounded-md bg-[hsl(var(--background-paper))]/60 animate-pulse" />
        ) : tripList.length === 0 ? (
          <SpotlightEmpty />
        ) : tripList.length === 1 ? (
          <SpotlightCard trip={singleTrip} />
        ) : (
          <ExperienceGallery trips={tripList} />
        )}
      </motion.div>

      <QuietStrip tripsLoading={trips === null} tripCount={tripList.length} />

      {/* Featured reminders are STRICTLY trip-scoped — only render when
          the client has exactly one experience so we can't accidentally
          surface another trip's reminders. With multiple trips, the
          client opens a specific experience first. */}
      {singleTrip && (
        <FeaturedRemindersBand tripId={singleTrip.id} />
      )}

      {/* Small calm CTA — opens Messages, the most-used "ping the advisor" affordance */}
      <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3">
        <PortalButton
          as={Link}
          to="/portal/client/messages"
          variant="ghost"
          size="sm"
          icon={<MessageSquare className="size-3.5" strokeWidth={1.5} />}
          data-testid="member-home-open-messages"
        >
          Open messages
        </PortalButton>
        <PortalButton
          as={Link}
          to="/portal/client/experiences"
          variant="ghost"
          size="sm"
          icon={<Compass className="size-3.5" strokeWidth={1.5} />}
          data-testid="member-home-open-experiences"
        >
          Browse experiences
        </PortalButton>
      </motion.div>
    </motion.div>
  );
}
